import React, { memo, useEffect, useState } from 'react';
import Modal from 'react-modal';
import dayjs from 'dayjs';
import { getCorporateInitiationsManagementTaskSettings } from '../../../../../services/libraries/corporate-library/initiations-management';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { utcToLocalDateFormatter } from '../../../../../utility/dates/utcToLocalDate';
import { convert24HourTo12HourFormat } from '../../../../../utility/dates/convert24HourTo12HourFormat';
import {
  all_months,
  frequencyOptions as frequencyTypeOptions,
  qualityFrequencyOptions,
  rangeFullOptions,
} from '../../../../init-form/settings/submition-settings-frequency/constants';
import { weekDays } from '../../../../init-form/settings/submition-settings-public/contstants';
import {
  NotifyUsersToFillTheFormPeriodOptions,
  NotifyUsersToFillTheFormStopPeriod,
  NotifyUsersToFillTheFormStopType,
  notificationOptions,
  statisticNotificationPeriodOptions,
  statisticNotificationStopPeriod,
  statisticNotificationStopType,
} from '../../../../../constants/forms/options';
import { formatDateValue } from '../../../form-management-modal/components/form-settings-modal/utils';
import { DATE_FORMAT } from '../../../../../constants';

import './styles.scss';

const getOptionLabel = (options, value) =>
  value === undefined || value === null ? '-' : options.find((o) => o.value === value)?.label ?? '-';

const formatRepeatEvery = (repeat, periodLabel) => {
  if (!repeat) return '-';
  const quantityLabel = repeat === 1 ? 'every' : `every ${repeat}`;
  return `${quantityLabel} ${periodLabel}`;
};

const joinNames = (items, getName) => (items?.length ? items.map(getName).join(', ') : null);

/**
 * Format a stored "YYYY-MM-DD" setting for display, without shifting it.
 *
 * The date the initiator picked is a wall-clock date in their own timezone, not an
 * instant, so it must be read back as written -- parsing it as a Date would put it at
 * UTC midnight and land on the previous day for anyone west of UTC.
 */
const formatPlainDate = (dateString) => {
  if (!dateString) return '-';

  const parsedDate = dayjs(`${dateString}T00:00:00`);

  return parsedDate.isValid() ? parsedDate.format(DATE_FORMAT) : '-';
};

const Option = ({ label, value }) => (
  <div className="option">
    <div className="option-label">{label}</div>
    <div className="option-value">{value}</div>
  </div>
);

/**
 * The settings of one run of a task, opened from the "Task view" of the Initiations management popup.
 *
 * The task counterpart of the Form settings popup: below the initiator it shows the forms the run
 * grouped and the settings the initiator gave the task when they ran it.
 */
const TaskSettingsModal = ({ taskInitiationId, onClose, setIsLoading }) => {
  const isOpen = Boolean(taskInitiationId);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!taskInitiationId) return;
    (async () => {
      try {
        setIsLoading(true);
        const response = await getCorporateInitiationsManagementTaskSettings(taskInitiationId);
        if (isResponseOk(response)) {
          setSettings(response.data);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [taskInitiationId, setIsLoading]);

  useEffect(() => {
    !isOpen && setSettings(null);
  }, [isOpen]);

  // Keep the listing behind the popup from scrolling under it.
  useEffect(() => {
    const parentModal = document.querySelector('.initiations-management-modal-overlay');
    if (parentModal) {
      parentModal.style.overflow = isOpen ? 'hidden' : 'auto';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const {
    task_name,
    initiator_full_name,
    initiator_position,
    initiation_date,
    creation_date,
    next_initiation,
    forms,
    submitters,
    submitter_facilities,
    notify_submitters,
    submitters_notification_type,
    submitters_notification_text,
    recipients,
    notify_recipients,
    recipients_notification_type,
    recipients_notification_text,
    notify_initiator,
    initiator_notification_type,
    initiator_notification_text,
    notification_settings,
    statistic_notification_settings,
    will_have_frequency,
    frequency_settings,
    will_have_scheduled_initiation,
    scheduled_initiation_settings,
    timezone,
  } = settings ?? {};

  const renderFormsIncluded = () => (
    <div className={'settings-block'}>
      <h3 className="settings-block-title">{'Forms included in the task'}</h3>
      {forms?.length ? (
        <ol className="task-forms-list">
          {forms.map(({ id, name }) => (
            <li key={id}>{name}</li>
          ))}
        </ol>
      ) : (
        <Option label={'Forms:'} value={'-'} />
      )}
    </div>
  );

  const renderSubmitters = () => {
    const {
      user_groups,
      individual_users,
      contacts,
      units,
      unregistered_submitters,
      has_strict_order_of_submissions,
      order_of_submitters,
    } = submitters ?? {};

    // The facilities the submitters are from -- also when they were picked by user group or by name.
    const facilityNames = joinNames(submitter_facilities, (item) => item.name);
    const userGroupNames = joinNames(user_groups, (item) => item.name);
    const userNames = joinNames(individual_users, (item) => `${item.first_name} ${item.last_name}`);
    const unitNames = joinNames(units, (item) => item.name);
    const contactNames = joinNames(contacts, (item) => `${item.first_name} ${item.last_name}`);
    const unregisteredNames = joinNames(
      unregistered_submitters,
      (item) => `${item.first_name} ${item.last_name}${item.email ? ` (${item.email})` : ''}`,
    );

    const hasAnySubmitter = [facilityNames, userGroupNames, userNames, unitNames, contactNames, unregisteredNames].some(Boolean);

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Submitters'}</h3>
        {facilityNames && <Option label={submitter_facilities.length === 1 ? 'Facility:' : 'Facilities:'} value={facilityNames} />}
        {userGroupNames && <Option label={'User groups:'} value={userGroupNames} />}
        {userNames && <Option label={'Individual users:'} value={userNames} />}
        {unitNames && <Option label={'Units:'} value={unitNames} />}
        {contactNames && <Option label={'Contacts:'} value={contactNames} />}
        {unregisteredNames && <Option label={'Unregistered submitters:'} value={unregisteredNames} />}
        {!hasAnySubmitter && <Option label={'Submitters:'} value={'-'} />}
        {has_strict_order_of_submissions && (
          <Option
            label={'Order of submitters:'}
            value={order_of_submitters?.length ? order_of_submitters.map((item, index) => <div key={index}>{item}</div>) : '-'}
          />
        )}
        {notify_submitters && (
          <>
            <Option label={'Notification type:'} value={getOptionLabel(notificationOptions, submitters_notification_type)} />
            {submitters_notification_text && <Option label={'Notification text:'} value={submitters_notification_text} />}
          </>
        )}
      </div>
    );
  };

  const renderRecipients = () => {
    // When the recipients are to be from the initiator's facility, the backend sends only the
    // facility, user groups and users of the initiator's facility -- the ones the run is for.
    const { facilities, user_groups, individual_users, should_recipients_be_from_the_same_facility } = recipients ?? {};

    const facilityNames = joinNames(facilities, (item) => item.name);
    const userGroupNames = joinNames(user_groups, (item) => item.name);
    const userNames = joinNames(individual_users, (item) => `${item.first_name} ${item.last_name}`);

    if (!facilityNames && !userGroupNames && !userNames && !notify_recipients && !should_recipients_be_from_the_same_facility) return null;

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Recipient settings'}</h3>
        {facilityNames && <Option label={'Facilities:'} value={facilityNames} />}
        {userGroupNames && <Option label={'User groups:'} value={userGroupNames} />}
        {userNames && <Option label={'Individual users:'} value={userNames} />}
        <Option
          label={'Should recipients be from the same facility as the initiator?'}
          value={should_recipients_be_from_the_same_facility ? 'Yes' : 'No'}
        />
        {notify_recipients && (
          <>
            <Option label={'Notification type:'} value={getOptionLabel(notificationOptions, recipients_notification_type)} />
            {recipients_notification_text && <Option label={'Notification text:'} value={recipients_notification_text} />}
          </>
        )}
      </div>
    );
  };

  const renderInitiatorNotification = () => {
    if (!notify_initiator) return null;

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Task completion notification to the initiator'}</h3>
        <Option label={'Notification type:'} value={getOptionLabel(notificationOptions, initiator_notification_type)} />
        {initiator_notification_text && <Option label={'Notification text:'} value={initiator_notification_text} />}
      </div>
    );
  };

  const renderNotifyUsersToFillSettings = () => {
    if (!notification_settings?.should_notify_users_to_fill_the_form) return null;

    const {
      notify_users_to_fill_the_form_notification_type,
      notify_users_to_fill_the_form_notification_text,
      notify_users_to_fill_the_form_repeat,
      notify_users_to_fill_the_form_period,
      notify_users_to_fill_the_form_sending_week_days,
      notify_users_to_fill_the_form_sending_month_days,
      notify_users_to_fill_the_form_sending_time,
      notify_users_to_fill_the_form_stop_type,
      notify_users_to_fill_the_form_stop_date,
      notify_users_to_fill_the_form_stop_period,
      notify_users_to_fill_the_form_stop_frequency,
    } = notification_settings;
    const periodLabel = getOptionLabel(NotifyUsersToFillTheFormPeriodOptions, notify_users_to_fill_the_form_period);

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Fill out form reminders'}</h3>
        <Option label={'Notification type:'} value={getOptionLabel(notificationOptions, notify_users_to_fill_the_form_notification_type)} />
        {notify_users_to_fill_the_form_notification_text && (
          <Option label={'Notification text:'} value={notify_users_to_fill_the_form_notification_text} />
        )}
        <Option label={'Repeat every:'} value={formatRepeatEvery(notify_users_to_fill_the_form_repeat, periodLabel)} />
        {notify_users_to_fill_the_form_period === 'weekly' && Array.isArray(notify_users_to_fill_the_form_sending_week_days) && (
          <Option
            label={'Send on these days:'}
            value={notify_users_to_fill_the_form_sending_week_days.map((d) => weekDays[d]).join(', ') || '-'}
          />
        )}
        {notify_users_to_fill_the_form_period === 'monthly' && Array.isArray(notify_users_to_fill_the_form_sending_month_days) && (
          <Option label={'Send on days:'} value={notify_users_to_fill_the_form_sending_month_days.join(', ') || '-'} />
        )}
        {notify_users_to_fill_the_form_sending_time && <Option label={'Time:'} value={notify_users_to_fill_the_form_sending_time} />}
        <Option label={'Stop sending:'} value={getOptionLabel(NotifyUsersToFillTheFormStopType, notify_users_to_fill_the_form_stop_type)} />
        {notify_users_to_fill_the_form_stop_type === 'specific_date' && notify_users_to_fill_the_form_stop_date && (
          <Option label={'Stop date:'} value={formatDateValue(notify_users_to_fill_the_form_stop_date)} />
        )}
        {notify_users_to_fill_the_form_stop_type === 'interval' && (
          <Option
            label={'Stop after:'}
            value={`${notify_users_to_fill_the_form_stop_frequency ?? '-'} ${getOptionLabel(
              NotifyUsersToFillTheFormStopPeriod,
              notify_users_to_fill_the_form_stop_period,
            )}`}
          />
        )}
      </div>
    );
  };

  const renderStatisticsNotificationSettings = () => {
    if (!statistic_notification_settings?.should_notify_about_statistics) return null;

    const {
      statistic_notification_repeat,
      statistic_notification_period,
      statistic_notification_sending_week_days,
      statistic_notification_sending_month_days,
      statistic_notification_sending_time,
      statistic_notification_stop_type,
      statistic_notification_stop_date,
      statistic_notification_stop_period,
      statistic_notification_stop_frequency,
    } = statistic_notification_settings;
    const periodLabel = getOptionLabel(statisticNotificationPeriodOptions, statistic_notification_period);

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Statistics report sending'}</h3>
        <Option label={'Repeat every:'} value={formatRepeatEvery(statistic_notification_repeat, periodLabel)} />
        {statistic_notification_period === 'weekly' && Array.isArray(statistic_notification_sending_week_days) && (
          <Option
            label={'Send on these days:'}
            value={statistic_notification_sending_week_days.map((d) => weekDays[d]).join(', ') || '-'}
          />
        )}
        {statistic_notification_period === 'monthly' && Array.isArray(statistic_notification_sending_month_days) && (
          <Option label={'Send on days:'} value={statistic_notification_sending_month_days.join(', ') || '-'} />
        )}
        {statistic_notification_sending_time && <Option label={'Time:'} value={statistic_notification_sending_time} />}
        <Option label={'Stop sending:'} value={getOptionLabel(statisticNotificationStopType, statistic_notification_stop_type)} />
        {statistic_notification_stop_type === 'specific_date' && statistic_notification_stop_date && (
          <Option label={'Stop date:'} value={formatDateValue(statistic_notification_stop_date)} />
        )}
        {statistic_notification_stop_type === 'interval' && (
          <Option
            label={'Stop after:'}
            value={`${statistic_notification_stop_frequency ?? '-'} ${getOptionLabel(
              statisticNotificationStopPeriod,
              statistic_notification_stop_period,
            )}`}
          />
        )}
      </div>
    );
  };

  const renderScheduledInitiation = () => {
    if (!will_have_scheduled_initiation) return null;

    const { date, time } = scheduled_initiation_settings ?? {};

    return (
      <>
        <Option label={'Scheduled initiation date:'} value={formatPlainDate(date)} />
        <Option label={'Scheduled initiation time:'} value={convert24HourTo12HourFormat(time) ?? '-'} />
        {timezone && <Option label={'Timezone:'} value={timezone} />}
      </>
    );
  };

  const renderInitiationSettings = () => {
    const { frequency_type, time, times, type, specific_dates, first_initiation_day, last_initiation_day, week_days, month_day, months } =
      frequency_settings ?? {};

    // A run scheduled for a specific time goes out later than it was created, so the date
    // at the top of the block is the moment the initiator ran the task -- the moment it
    // goes out at is the scheduled date and time below it.
    const dateLabel = will_have_scheduled_initiation ? 'Initiation creation date:' : 'Initiation date:';
    const dateValue = will_have_scheduled_initiation ? creation_date : initiation_date;

    return (
      <div className={'settings-block'}>
        <h3 className="settings-block-title">{'Task initiation settings'}</h3>
        <Option label={dateLabel} value={dateValue ? utcToLocalDateFormatter(dateValue) : '-'} />
        <Option label={'Has a one-time initiation scheduled for a specific time?'} value={will_have_scheduled_initiation ? 'Yes' : 'No'} />
        {renderScheduledInitiation()}
        <Option label={'Has frequency?'} value={will_have_frequency ? 'Yes' : 'No'} />

        {will_have_frequency && (
          <>
            <Option label={'Next initiation:'} value={next_initiation ? utcToLocalDateFormatter(next_initiation, 'MMMM D, h:mm A') : '-'} />
            <Option label={'Frequency type:'} value={getOptionLabel(frequencyTypeOptions, frequency_type)} />

            {frequency_type === 'specific_dates' && (
              <Option
                label={'Initiation dates:'}
                value={Array.isArray(specific_dates) ? specific_dates.map((dateString) => formatDateValue(dateString)).join('; ') : '-'}
              />
            )}

            {frequency_type === 'custom_interval' && (
              <>
                <Option label={'First initiation day:'} value={first_initiation_day || '-'} />
                <Option label={'Last initiation day:'} value={last_initiation_day || '-'} />
                <Option label={'Period quality:'} value={getOptionLabel(qualityFrequencyOptions, times)} />
                <Option label={'Period:'} value={getOptionLabel(rangeFullOptions, type)} />
                {type === 'weekly' && (
                  <Option
                    label={'Repeat on these days:'}
                    value={Array.isArray(week_days) ? week_days.map((dayIndex) => weekDays[dayIndex]).join('; ') : '-'}
                  />
                )}
                {type === 'yearly' && (
                  <Option
                    label={'Repeat on these months:'}
                    value={Array.isArray(months) ? months.map((monthNum) => all_months[monthNum - 1]).join('; ') : '-'}
                  />
                )}
                {(type === 'monthly' || type === 'yearly') && (
                  <Option label={'Initiation days:'} value={Array.isArray(month_day) ? month_day.join('; ') : '-'} />
                )}
              </>
            )}

            {(frequency_type === 'specific_dates' || frequency_type === 'custom_interval') && (
              <Option label={'Initiation time:'} value={time || '-'} />
            )}
            {timezone && <Option label={'Timezone:'} value={timezone} />}
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      className="modal-default task-settings-modal"
      overlayClassName={'task-settings-modal-overlay'}
      onRequestClose={onClose}
    >
      <h2 className="modal-header">
        {'Task Settings'}
        <button className="btn btn-icon" onClick={onClose}>
          <i className="icon-icon-x" />
        </button>
      </h2>
      <div className="modal-body">
        {settings && (
          <>
            <h3>
              <strong>Task:</strong> {task_name}
            </h3>
            <h3>
              <strong>Initiator:</strong> {initiator_full_name} {initiator_position ? `(${initiator_position})` : ''}
            </h3>
            {renderFormsIncluded()}
            {renderInitiationSettings()}
            {renderSubmitters()}
            {renderRecipients()}
            {renderInitiatorNotification()}
            {renderNotifyUsersToFillSettings()}
            {renderStatisticsNotificationSettings()}
          </>
        )}
      </div>
    </Modal>
  );
};

export default memo(TaskSettingsModal);
